import CalendarItemsList from '@efdigital/ll-addon-content/components/calendar/calendar-items-list';
import type { CalendarItem } from '@efdigital/ll-addon-data';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditCalendarItem } from '@efdigital/ll-admin-v2/components/programs/calendar/edit-calendar-item';
import MessagesList from '@efdigital/ll-admin-v2/components/programs/messages/messages-list.gts';
import ScheduleBreadcrumb from '@efdigital/ll-admin-v2/components/programs/schedule-breadcrumb.gts';
import { findCalendarItem } from '@efdigital/ll-admin-v2/data/builders/xceed/calendar-items';
import { queryMessagesByCalendarItem } from '@efdigital/ll-admin-v2/data/builders/xceed/messages';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramCohortScheduleItemRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort/schedule/calendar-item';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { cacheKeyFor } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import { serializePatch } from '@warp-drive/utilities/json-api';
import { updateRecord } from '@warp-drive/utilities/json-api';
import { pageTitle } from 'ember-page-title';

interface CalendarItemSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortScheduleItemRoute>;
    controller: unknown;
  };
}

export default class ProgramsProgramCohortScheduleItemTemplate extends Component<CalendarItemSignature> {
  @service declare store: Store;

  updateCalendarItem = (
    currentCalendarItem: CalendarItem,
    updates: Partial<CalendarItem>
  ) => {
    Object.assign(currentCalendarItem, updates);

    const init = updateRecord(currentCalendarItem, {
      ...jsonApiOptions,
      patch: true,
    });
    init.body = JSON.stringify(
      serializePatch(this.store.cache, cacheKeyFor(currentCalendarItem))
    );
    init.headers.set('Content-Type', 'application/vnd.api+json');

    return this.store.request(init);
  };

  <template>
    {{pageTitle "Event"}}
    <ScheduleBreadcrumb @model={{@model}} />

    <div class="container py-4">
      <EditContext as |context|>
        <Request @query={{findCalendarItem @model.calendar_item_id}}>
          <:content as |content|>
            <div class="row">
              <div class="col-md-6 py-3">
                <EditCalendarItem
                  @item={{content.data}}
                  @context={{context}}
                  @showEventFields={{true}}
                  @showContentFields={{true}}
                />
              </div>
              <div class="col-md-6">
                <div class="sticky-top py-3">
                  <h3 class="gud-eyebrow">Preview</h3>
                  <CalendarItemsList @calendarItems={{array content.data}} />

                  {{#if content.data.id}}
                    <fieldset disabled={{true}} class="mb-4">
                      <legend class="gud-eyebrow">Associated Announcements</legend>
                      <p>Announcements can be associated with events to provide
                        additional information or context. To do this,
                        <LinkTo
                          @route="programs.program.cohort.schedule.index"
                          @models={{array @model.program_id @model.cohort_id}}
                        >find and edit the announcement</LinkTo>
                        and select the event from the list.
                      </p>

                      <Request
                        @query={{queryMessagesByCalendarItem content.data.id}}
                      >
                        <:content as |announcements|>
                          <div class="mb-3">
                            {{#if announcements.data.length}}
                              <MessagesList
                                @messages={{announcements.data}}
                                @category="announcement"
                                @programId={{@model.program_id}}
                                @cohortId={{@model.cohort_id}}
                              />
                            {{else}}
                              <div class="alert alert-info">
                                No announcements associated with this event.
                              </div>
                            {{/if}}
                          </div>
                        </:content>
                      </Request>
                    </fieldset>
                  {{/if}}
                </div>
              </div>
            </div>
          </:content>
        </Request>
      </EditContext>
    </div>
  </template>
}
